import MailboxTableHeader from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTableHeader';
import {
  getAircraftRegistrationColumnProps,
  getArrivalAirportColumnProps,
  getDepartureAirportColumnProps,
  getFlightDateColumnProps,
  getFlightNumberColumnProps,
  getHasIncapacitatedCrewColumnProps,
  getPositionAssignmentPurserColumnProps,
} from '@src/main/modules/reports/ui/components/table/ReportsColumns';

import ReportsTable from '@src/main/modules/reports/ui/components/table/ReportsTable';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { getThreeMonthDateRange } from '@src/main/shared/utils/dateUtils';

import {
  PositionAssignmentReport,
  PositionAssignmentRequest,
} from '@src/main/modules/reports/shared/domain/report.interface';
import { Column } from 'primereact/column';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePositionAssignmentReports } from '@src/main/modules/reports/positionassignment/application/hook/usePositionAssignmentReports';
import { usePositionAssignmentDateFilter } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentDateFilter';
import { usePositionAssignmentFilters } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentFilters';

import { usePositionAssignmentActions } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentActions';
import { usePositionAssignmentColumnBodies } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentColumnBodies';
import { usePositionAssignmentExport } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentExport';
import { usePositionAssignmentFilterHandler } from '@src/main/modules/reports/ui/hooks/usePositionAssignmentFilterHandler';
import { mapPositionAssignmentFiltersToCriteria } from '@src/main/modules/reports/ui/mapPositionAssignmentToCriteria';
import styles from './PunctualityView.module.scss';

const PositionAssignmentTabView: FC = () => {
  const { t } = useTranslation();

  const { startDate, endDate } = getThreeMonthDateRange();

  const [filterValues] = useState({
    flightNumber: null as string | null,
    departureAirport: null as string | null,
    arrivalAirport: null as string | null,
    aircraftRegistration: null as string | null,
    purserEmployeeId: null as string | null,
  });

  const initialFilters = usePositionAssignmentFilters({
    startDate,
    endDate,
    flightNumber: filterValues.flightNumber,
    departureAirport: filterValues.departureAirport,
    arrivalAirport: filterValues.arrivalAirport,
    aircraftRegistration: filterValues.aircraftRegistration,
    purserEmployeeId: filterValues.purserEmployeeId,
  });

  const [invalidDateModalVisible, setInvalidDateModalVisible] = useState(false);

  const emptyCriteria: PositionAssignmentRequest = {
    flightNumber: null,
    flightDate: null,
    departureAirport: null,
    arrivalAirport: null,
    aircraftRegistration: null,
    purserEmployeeId: null,
    flightSince: null,
    flightUntil: null,
    sortField: 'flight_date',
    sortOrder: 'desc',
    offset: 0,
    limit: 10,
  };

  const {
    filters,
    setFilters,
    reportsRaw,
    reports,
    total,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    handleSort,
    sortField,
    sortOrder,
    refetchReports,
  } = usePositionAssignmentReports(
    initialFilters,
    mapPositionAssignmentFiltersToCriteria,
    emptyCriteria,
  );

  const { handleSetFilters } = usePositionAssignmentFilterHandler(
    setFilters,
    refetchReports,
    setInvalidDateModalVisible,
  );

  const positionAssignmentDateFilterTemplate = usePositionAssignmentDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const downloadPdf = usePositionAssignmentActions();

  const pdfColumnBody = usePositionAssignmentColumnBodies(t, downloadPdf);

  const { onExportExcel } = usePositionAssignmentExport(reportsRaw, 'informe_position_assignment');

  const header = (
    <MailboxTableHeader
      onRefresh={refetchReports}
      onClearFilters={() => setFilters(initialFilters)}
      onExportExcel={onExportExcel}
      showExportButtons
    />
  );

  return (
    <div className={styles.container}>
      <AppLoading isLoading={isLoading || isFetching}>
        <ReportsTable<PositionAssignmentReport>
          data={reports}
          filters={filters}
          setFilters={handleSetFilters}
          header={header}
          loading={isLoading || isFetching}
          dataKey="id"
          globalFilterFields={[
            'flight_number',
            'flight_date',
            'departure_airport',
            'arrival_airport',
            'aircraft_registration',
            'purserEmployeeId',
          ]}
          totalRecords={total}
          first={pageNumber * pageSize}
          rows={pageSize}
          onPage={handlePageChange}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
        >
          <Column {...getFlightDateColumnProps(t, positionAssignmentDateFilterTemplate)} />
          <Column {...getFlightNumberColumnProps(t)} />
          <Column {...getDepartureAirportColumnProps(t)} />
          <Column {...getArrivalAirportColumnProps(t)} />
          <Column {...getAircraftRegistrationColumnProps(t)} />
          <Column {...getHasIncapacitatedCrewColumnProps(t)} />
          <Column {...getPositionAssignmentPurserColumnProps(t)} />
          <Column body={pdfColumnBody} header={t('common.pdf')} />
        </ReportsTable>

        <InvalidDateRangeDialog
          invalidDateRangeModalVisible={invalidDateModalVisible}
          setInvalidDateRangeModalVisible={setInvalidDateModalVisible}
          t={t}
        />
      </AppLoading>
    </div>
  );
};

export default PositionAssignmentTabView;
